import { dateText, dateTimeText } from '../date/local-time'
import { expiresAtFor } from '../../layout/authorized/org/api-keys/key-words'
import { KEEP_EXPIRY, METHOD_OAUTH, TOKEN_EXPIRY } from './scim-words'

// The words of the SCIM link dialogs (owner 2026-10-29: "nicer styling
// structure/alignment"): the choices as segmented options and a live
// summary of what will happen, in the viewer's own zone. The values sent are
// unchanged: grace_hours 0, 1, 24 or 168; expires_at from expiresAtFor (or
// nothing to keep the current date).

const HOUR = 36e5

// How long the old credential keeps working, in the order offered (the
// default first after the shortest): 1 hour / 24 hours / 7 days / Stop now.
export const GRACE_OPTIONS = [
  { value: '1', label: '1 hour' },
  { value: '24', label: '24 hours' },
  { value: '168', label: '7 days' },
  { value: '0', label: 'Stop now' }
]

// The expiry's segmented options for a rules set ({ choices }): the rules'
// own, "Custom date" read as "Pick a date"; with `keep` (a rotate or a switch
// back to the Bearer token), "Keep <date>" first.
export const expiryOptions = (rules, { keep = false, token = null } = {}) => {
  const options = rules.choices.map(choice => ({ value: choice.value, label: choice.value === 'custom' ? 'Pick a date' : choice.label }))
  if (!keep) return options
  const current = token && token.expiresAt ? dateText(token.expiresAt) : 'never'
  return [{ value: KEEP_EXPIRY, label: token && token.expiresAt ? `Keep ${current}` : 'Keep: never' }, ...options]
}

// Whether a rotate (or a switch) may keep the current date: a Bearer token
// always may; an OAuth client secret only when it has one (it can't be
// never).
export const canKeepExpiry = (rules, token) => rules === TOKEN_EXPIRY || Boolean(token && token.expiresAt)

// What an expiry choice gives: { at } (an ISO time), { never: true },
// { keep: true, at | never } or { error } (a custom date not picked yet, or
// out of range).
export const expiryOutcome = ({ choice, customDay, rules, token, now = Date.now() }) => {
  if (choice === KEEP_EXPIRY) {
    return token && token.expiresAt ? { keep: true, at: token.expiresAt } : { keep: true, never: true }
  }
  const expires = expiresAtFor(choice, customDay, now, rules)
  if (expires.error) return { error: expires.error.replace(/\bkey\b/g, 'token') }
  return expires.value === null ? { never: true } : { at: expires.value }
}

// "Oct 30 at 3:12 PM MDT": when the old credential stops, in the viewer's
// zone (with the year when it isn't this year's).
export const graceEnd = (hours, now = Date.now()) => {
  if (!hours) return ''
  const at = now + Number(hours) * HOUR
  const sameYear = new Date(at).getFullYear() === new Date(now).getFullYear()
  return dateTimeText(at, { year: !sameYear })
}

const expirySentence = (subject, outcome) => {
  if (!outcome) return ''
  if (outcome.error) return `Pick the date ${subject} expires.`
  if (outcome.never) return `${capital(subject)} never expires.`
  return `${capital(subject)} expires ${dateText(outcome.at)}.`
}

const graceSentence = (subject, hours, verb, now) =>
  Number(hours) === 0
    ? `${capital(subject)} stops working as soon as you ${verb}.`
    : `${capital(subject)} stops working ${graceEnd(hours, now)}.`

const capital = s => s.charAt(0).toUpperCase() + s.slice(1)

// The turn-on dialog's summary: "The token expires Oct 29, 2027."
export const turnOnSummary = ({ method, outcome }) =>
  expirySentence(method === METHOD_OAUTH ? 'the client secret' : 'the token', outcome)

// The rotate dialog's summary: "The old token stops working Oct 30 at
// 3:12 PM MDT. The new token expires Oct 26, 2027."
export const rotateSummary = ({ noun, graceHours, outcome, now = Date.now() }) =>
  [graceSentence(`the old ${noun}`, graceHours, 'rotate', now), expirySentence(`the new ${noun}`, outcome)].join(' ')

// The switch dialog's summary, naming both credentials: "The Bearer token
// stops working Oct 30 at 3:12 PM MDT. The new client secret expires Oct 29,
// 2027."
export const switchSummary = ({ to, graceHours, outcome, now = Date.now() }) => {
  const old = to === METHOD_OAUTH ? 'the Bearer token' : 'the client secret and its access tokens'
  const made = to === METHOD_OAUTH ? 'the new client secret' : 'the new token'
  const stops = Number(graceHours) === 0
    ? `${capital(old)} ${to === METHOD_OAUTH ? 'stops' : 'stop'} working as soon as you switch.`
    : `${capital(old)} ${to === METHOD_OAUTH ? 'keeps' : 'keep'} working until ${graceEnd(graceHours, now)}.`
  return [stops, expirySentence(made, outcome)].join(' ')
}

// What a switch does, as three short steps.
export const switchSteps = ({ to, graceHours, now = Date.now() }) => {
  const until = Number(graceHours) === 0 ? null : graceEnd(graceHours, now)
  if (to === METHOD_OAUTH) {
    return [
      'EducationXR creates a client ID and secret, shown once.',
      'You paste them, with the token endpoint, into your identity provider.',
      until ? `The current Bearer token keeps working until ${until}.` : 'The current Bearer token stops working at once.'
    ]
  }
  return [
    'EducationXR creates a Bearer token, shown once.',
    'You paste it into your identity provider in place of the client ID and secret.',
    until ? `The current client secret and its access tokens keep working until ${until}.` : 'The current client secret and its access tokens stop working at once.'
  ]
}

// The shown-once dialog's line after a rotation or a switch: how long the
// old credential still works ("The old token keeps working for 24 hours,
// until Oct 30 at 3:12 PM MDT."); '' after a turn-on. The new expiry is the
// chip under the secret.
export const shownSummary = ({ graceHours, graceNoun = 'token', now = Date.now() }) => {
  if (graceHours === undefined || graceHours === null) return ''
  if (Number(graceHours) === 0) return `The old ${graceNoun} has stopped working.`
  const option = GRACE_OPTIONS.find(o => Number(o.value) === Number(graceHours))
  return `The old ${graceNoun} keeps working for ${option ? option.label : `${graceHours} hours`}, until ${graceEnd(graceHours, now)}.`
}
